import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI } from '../services/api';
import { FiRefreshCw, FiAlertTriangle, FiLayers, FiArrowLeft, FiX } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

const TYPE_LABELS = {
  IN_HOUSE: 'In-House',
  SUB_AGENT: 'Sub-Agent',
  INDIVIDUAL_AGENT: 'Individual Agent',
  PARTNER_SCHOOL: 'Institution',
  HOSPITAL: 'Hospital',
};

// Same numbers the partner sees in their own PARTNER_PORTAL "My Analytics"
// page — both surfaces are backed by users.views._partner_statistics, so a
// card here and that partner's own dashboard can never drift apart.
const primaryTotal = (stat) => {
  if (stat.medical_clients) return { label: 'Medical Clients', value: stat.medical_clients.total };
  return { label: 'Students', value: stat.students?.total ?? 0 };
};

const DetailPanel = ({ stat, onBack }) => {
  if (!stat) return null;
  const agent = stat.agent || {};
  return (
    <div className="students-table-container" style={{ padding: 20 }}>
      <button className="cv-inline-btn" onClick={onBack} style={{ marginBottom: 16 }}>
        <FiArrowLeft size={13} /> Back to all partners
      </button>
      <h2 style={{ margin: '0 0 4px', fontSize: 18, color: '#1e293b' }}>
        {agent.first_name || agent.last_name ? `${agent.first_name || ''} ${agent.last_name || ''}`.trim() : agent.username}
      </h2>
      <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: 13 }}>
        {TYPE_LABELS[stat.agency_type] || stat.agency_type || 'No agency'} · {agent.email}
      </p>

      {stat.students && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Students</span></div>
          <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>
            Total: <strong>{stat.students.total}</strong> · Enrolled: <strong>{stat.students.enrolled}</strong>
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#334155' }}>
            Commission — Total: <strong>${stat.students.total_commission.toFixed(2)}</strong> · Paid: <strong style={{ color: '#43e97b' }}>${stat.students.paid_commission.toFixed(2)}</strong>
          </p>
        </div>
      )}
      {stat.job_clients && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Job Seekers</span></div>
          <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>
            Total: <strong>{stat.job_clients.total}</strong> · Placed: <strong>{stat.job_clients.placed}</strong>
          </p>
        </div>
      )}
      {stat.tourist_clients && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Tourists</span></div>
          <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>
            Total: <strong>{stat.tourist_clients.total}</strong> · Completed: <strong>{stat.tourist_clients.completed}</strong>
          </p>
        </div>
      )}
      {stat.medical_clients && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Medical Tourism</span></div>
          <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>
            Total: <strong>{stat.medical_clients.total}</strong> · Completed: <strong>{stat.medical_clients.completed}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

const PartnerAnalytics = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await usersAPI.partnerStatistics();
      setPartners(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load partner analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (agentId) => {
    setDetailLoading(true);
    try {
      const res = await usersAPI.agentDetailStatistics(agentId);
      setSelected(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load partner detail.');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiLayers size={22} color="#011ff3" />
            Partner Analytics
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
            Referral numbers per agent, institution, and hospital. <strong style={{ color: '#64748b' }}>{partners.length}</strong> partners.
          </p>
        </div>
        {!selected && (
          <button className="btn-icon" onClick={load} title="Refresh">
            <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
          </button>
        )}
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiAlertTriangle size={16} />
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={14} /></button>
        </div>
      )}

      {selected || detailLoading ? (
        detailLoading ? (
          <div className="students-table-container"><div className="empty-state">Loading…</div></div>
        ) : (
          <DetailPanel stat={selected} onBack={() => setSelected(null)} />
        )
      ) : loading ? (
        <div className="students-table-container"><div className="empty-state">Loading…</div></div>
      ) : partners.length === 0 ? (
        <div className="students-table-container"><div className="empty-state">No active agents/institutions/hospitals yet.</div></div>
      ) : (
        <div className="partner-grid">
          {partners.map((stat) => {
            const total = primaryTotal(stat);
            return (
              <div className="partner-card" key={stat.agent_id} onClick={() => openDetail(stat.agent_id)}>
                <div className="partner-card-name">{stat.agent_name}</div>
                <div className="partner-card-sub">{TYPE_LABELS[stat.agency_type] || stat.agency_type || 'No agency'}</div>
                <div className="partner-card-total">{total.value}</div>
                <div className="partner-card-sub">{total.label}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PartnerAnalytics;
